import { Agencies, ITEMS_PER_PAGE, ServiceTypes } from '../helpers/Constants';
import Routes from '../helpers/Routes';
import ServiceHandler, { serviceBasedRoutes } from '../helpers/ServiceHandler';
import { ISyncRecordRequest } from '../models';
import {
  IGetCompleteContractParams,
  IReplacementsParams,
} from '../models/Models';
import {
  ICalendarFilter,
  ICancelBooking,
  ICancelMultipleBookingParams,
  IHistoryApiParams,
  IHistoryParams,
  IIncomeDetailsParams,
  IIncomeReportParams,
  IMarkAsMultiplePaidParams,
  IPreSubmitVacationsParams,
  IReportFilterParams,
  IReportOmitAvailability,
  IReportsPDFParams,
  IRequestBookingParams,
  IRequestContractModel,
  IUpdateVacationsParams,
  IVacationsParams,
  IWorkshiftParams,
  IWorkshiftRequestModel,
  IWorkshiftWatchlistModel,
} from '../models/PharmacistModels';
import httpClient from './httpClient';
import {
  ICreateAvailabilityRequestParams,
  IUpdateAvailabilitRequestParams,
} from './types';

class PharmacistController {
  basePath: string;

  isSelfService = ServiceHandler.getService() === ServiceTypes.self;
  constructor() {
    this.basePath = this.isSelfService
      ? Routes.professional.professionalBasePath
      : Routes.pharmacist.pharmacistBasePath;
  }

  pharmacistCalendar = async (filter: ICalendarFilter) =>
    httpClient.post(`${this.basePath}/${Routes.pharmacist.calendar}`, {
      calRequest: {
        ...filter,
      },
    });

  pharmacistPostedShifts = async (id: string) =>
    httpClient.get(`${this.basePath}${Routes.pharmacist.posted}/${id}`);

  pharmacistFilterValues = async () =>
    httpClient.get(
      `${this.basePath}${serviceBasedRoutes.pharmacistCalendar}`,
      {}
    );

  pharmacistPicture = async () =>
    httpClient.get(`${this.basePath}${Routes.pharmacist.picture}`);

  pharmacistSummary = async (month: number) =>
    httpClient.post(`${this.basePath}${Routes.pharmacist.summary}`, {
      month: month.toString(),
    });

  getAvailability = async (id: string) => {
    return httpClient.get(
      `${this.basePath}${Routes.pharmacist.availabilites.dateRangeAvailability}/${id}`
    );
  };

  pharmacistMap = async (filter: ICalendarFilter) =>
    httpClient.post(`${this.basePath}${Routes.pharmacist.map}`, {
      calRequest: {
        ...filter,
      },
    });

  createAvailability = async (params: ICreateAvailabilityRequestParams) => {
    return httpClient.post(
      `${this.basePath}${Routes.pharmacist.availabilites.create}`,
      {
        multipleAvailabilityModel: {
          ...params,
        },
      }
    );
  };

  updateAvailability = async (params: IUpdateAvailabilitRequestParams) => {
    return httpClient.post(
      `${this.basePath}${Routes.pharmacist.availabilites.update}`,
      {
        multipleAvailabilityModel: {
          ...params.data,
          availabilityId: params.availabilityId,
          recurrenceId: params.recurrenceId || null,
        },
      }
    );
  };
  deleteAvailability = async (id?: string, recurrenceId?: string) =>
    httpClient.post(
      `${this.basePath}${Routes.pharmacist.availabilites.delete}`,
      {
        availableTimeId: id,
        recurrenceId: recurrenceId || null,
      }
    );

  requestBooking = async (params: IRequestBookingParams) =>
    httpClient.post(
      `${this.basePath}${Routes.pharmacist.bookings.submitRequest}`,
      {
        workshiftid: params.workshiftid,
        counterOffer: params?.counterOffer,
        isAutoSyncRecord: params?.isAutoSyncRecord,
      }
    );

  cancelBooking = async (params: ICancelBooking) =>
    httpClient.post(`${this.basePath}${Routes.general.cancelRequest}`, params);

  cancelRequestedBooking = async ({
    id: bookingIdArr,
    isAutoSyncRecord,
  }: ISyncRecordRequest) =>
    httpClient.post(
      `${this.basePath}${Routes.pharmacist.bookings.deleteRequest}`,
      {
        list: [bookingIdArr],
        isAutoSyncRecord,
      }
    );

  reports = async (params: IReportFilterParams) =>
    httpClient.post(`${this.basePath}${Routes.pharmacist.reports}`, {
      reportFilters: { ...params, itemPerPage: ITEMS_PER_PAGE },
    });

  replacements = async (params: IReplacementsParams) =>
    httpClient.post(`${this.basePath}${Routes.pharmacist.replacements}`, {
      source: params.source,
      pagingModel: {
        itemPerPage: ITEMS_PER_PAGE,
        ...params,
      },
    });

  history = async (params: IHistoryApiParams) =>
    httpClient.post(`${this.basePath}${Routes.pharmacist.history}`, {
      ...params,
      source: params?.source,
      paging: {
        ...params.paging,
        itemPerPage: ITEMS_PER_PAGE,
      },
    });

  addFavourite = async (params: IHistoryParams) =>
    httpClient.post(
      `${this.basePath}${Routes.pharmacist.addFavourite}`,
      params
    );

  removeFavourite = async (params: IHistoryParams) =>
    httpClient.post(
      `${this.basePath}${Routes.pharmacist.removeFavourite}`,
      params
    );

  pharmacyNames = async () =>
    httpClient.get(`${this.basePath}${Routes.pharmacist.replacements}`);

  reportsFilterInfo = async (type: IReportOmitAvailability) =>
    httpClient.get(`${this.basePath}${Routes.pharmacist.reports}/${type}`);

  pictureUpload = async (form: any) =>
    httpClient.post(
      `${Routes.pharmacist.fileupload}${
        this.isSelfService
          ? Routes.professional.professionalBasePath
          : Routes.pharmacist.pharmacist
      }`,
      form,
      { headers: { 'Content-Type': 'multipart/form-data' } }
    );

  deletePicture = async () =>
    httpClient.delete(`${this.basePath}${Routes.pharmacist.picture}`);

  resumeUpload = async (form: { document: Buffer; path: string }) =>
    httpClient.post(
      `${this.basePath}${Routes.pharmacist.resume}?path=${form.path}`,
      form.document,
      {
        headers: { 'Content-Type': 'application/pdf' },
      }
    );

  pharmacistResume = async () =>
    httpClient.get(`${this.basePath}${Routes.pharmacist.resume}`);

  deleteResume = async () =>
    httpClient.delete(`${this.basePath}${Routes.pharmacist.resume}`);

  createNote = async (pharmacyId: string, note: string) =>
    httpClient.post(`${this.basePath}${Routes.pharmacist.notes.createNote}`, {
      pharmacyId: pharmacyId,
      notes: note,
    });

  allNotes = async (pharmacyId: string) =>
    httpClient.post(`${this.basePath}${Routes.pharmacist.notes.allNotes}`, {
      pharmacyId: pharmacyId,
    });

  updateNote = async (noteId: string, note: string) =>
    httpClient.post(`${this.basePath}${Routes.pharmacist.notes.updateNote}`, {
      pharmacyNotesId: noteId,
      notes: note,
    });

  deleteNote = async (noteId: string) =>
    httpClient.post(`${this.basePath}${Routes.pharmacist.notes.deleteNote}`, {
      pharmacyNotesId: noteId,
    });

  getRegions = async () =>
    httpClient.get(`${this.basePath}${Routes.pharmacist.regions.region}`);

  updateRegions = async (regionIds: Array<string>) =>
    httpClient.post(`${this.basePath}${Routes.pharmacist.regions.region}`, {
      regions: regionIds,
    });

  createRegion = async (regionId: string) =>
    httpClient.post(
      `${this.basePath}${Routes.pharmacist.regions.createRegion}`,
      {
        regionId,
      }
    );
  deleteRegion = async (regionId: string) =>
    httpClient.delete(
      `${this.basePath}${Routes.pharmacist.regions.region}/${regionId}`
    );

  getReplacementsPDF = async (source: Agencies) =>
    httpClient.get(
      `${this.basePath}${Routes.pharmacist.replacements}${Routes.general.getPDF}`,
      {
        params: { source },
      }
    );

  getReportsPdf = async (params: IReportsPDFParams) =>
    httpClient.post(
      `${this.basePath}/${params.reportFilters.type}${Routes.general.getPDF}`,
      params
    );

  getCounterOfferDetails = async (bookingId: string) =>
    httpClient.get(
      `${this.basePath}${Routes.pharmacist.counterOffer}/${bookingId}`
    );

  acceptUpdatedTimings = async (bookingId: string) =>
    httpClient.post(
      `${this.basePath}${Routes.general.bookings}${Routes.pharmacy.updateTime}${Routes.pharmacy.approve}`,
      { bookingId }
    );

  rejectUpdatedTimings = async (bookingId: string) =>
    httpClient.post(
      `${this.basePath}${Routes.general.bookings}${Routes.pharmacy.updateTime}${Routes.pharmacy.reject}`,
      { bookingId }
    );

  selfPharmacistCancelBooking = async ({
    id: bookingId,
    isAutoSyncRecord,
  }: ISyncRecordRequest) =>
    httpClient.post(
      `${this.basePath}${Routes.general.bookings}${Routes.general.cancel}`,
      { bookingId, isAutoSyncRecord }
    );

  preRequest = async (params: IWorkshiftRequestModel) =>
    httpClient.post(
      `${this.basePath}${Routes.pharmacist.bookings.preBooking}`,
      params
    );

  income = async (params: IIncomeReportParams) =>
    httpClient.post(`${this.basePath}${Routes.pharmacist.income}`, {
      criteria: { ...params, itemPerPage: ITEMS_PER_PAGE },
    });

  incomeDetails = async (params: IIncomeDetailsParams) =>
    httpClient.post(`${this.basePath}${Routes.pharmacist.incomeDetails}`, {
      criteria: { ...params, itemPerPage: ITEMS_PER_PAGE },
    });

  getEvent = async (params: IWorkshiftParams) =>
    httpClient.post(`${this.basePath}${Routes.general.workshifts}`, params);

  watchlist = async (params: IWorkshiftWatchlistModel) =>
    httpClient.post(`${this.basePath}${Routes.pharmacist.watchlist}`, params);

  deleteWatchlist = async ({
    workshiftId,
    isAutoSyncRecord,
  }: IWorkshiftWatchlistModel) =>
    httpClient.delete(
      `${this.basePath}${Routes.pharmacist.watchlist}/${workshiftId}`,
      {
        params: {
          isAutoSyncRecord,
        },
      }
    );

  preSubmitVacations = async (params: IPreSubmitVacationsParams) =>
    httpClient.post(
      `${this.basePath}${Routes.pharmacist.vacations}${Routes.general.preSubmit}`,
      {
        model: params,
      }
    );

  pharmacistVacations = async (params: IVacationsParams) =>
    httpClient.post(`${this.basePath}${Routes.pharmacist.vacations}`, {
      model: params,
    });

  getPharmacistVacations = async (id: string) =>
    httpClient.get(`${this.basePath}${Routes.pharmacist.vacations}/${id}`);

  deletePharmacistVacations = async (id: string) =>
    httpClient.delete(`${this.basePath}${Routes.pharmacist.vacations}/${id}`);

  updatePharmacistVacations = async (params: IUpdateVacationsParams) =>
    httpClient.put(`${this.basePath}${Routes.pharmacist.vacations}`, {
      model: params,
    });

  preSubmitAvailabilities = async (params: ICreateAvailabilityRequestParams) =>
    httpClient.post(
      `${this.basePath}${Routes.general.availabilities}${Routes.general.preSubmit}`,
      {
        multipleAvailabilityModel: params,
      }
    );

  getWorkshiftsByDate = async (date: string) =>
    httpClient.get(`${this.basePath}${Routes.general.workshifts}/${date}`);

  requestContract = async (params: IRequestContractModel) =>
    httpClient.post(
      `${this.basePath}${Routes.general.contract}${Routes.general.requests}${Routes.general.submit}`,
      params
    );

  cancelRequestContract = async (contractId: string) =>
    httpClient.post(
      `${this.basePath}${Routes.general.contract}${Routes.general.requests}${Routes.general.delete}`,
      {
        contractId,
      }
    );

  getProfessionalLongTermBookings = async (longTermContractId: string) =>
    httpClient.get(
      `${this.basePath}${Routes.general.bookings}/${longTermContractId}`
    );

  getProfessionalCompleteContract = async (
    params: IGetCompleteContractParams
  ) =>
    httpClient.post(
      `${this.basePath}${Routes.general.contract}${Routes.general.shifts}`,
      params
    );

  getWorkshiftStatus = async (workshiftId: string) =>
    httpClient.get(
      `${this.basePath}${Routes.general.workshifts}${Routes.general.status}/${workshiftId}`
    );

  cancelMultipleBookings = async (params: ICancelMultipleBookingParams) =>
    httpClient.post(
      `${this.basePath}${Routes.general.contract}${Routes.general.bookings}${Routes.general.cancel}`,
      params
    );

  markMultipleAsPaid = async (params: Array<IMarkAsMultiplePaidParams>) =>
    httpClient.post(
      `${this.basePath}${Routes.general.bookings}${Routes.general.multiple}${Routes.professional.markPaid}`,
      { model: params }
    );

  requestMultipleBookingsCancellation = (
    params: ICancelMultipleBookingParams
  ) =>
    httpClient.post(
      `${this.basePath}${Routes.general.contract}${Routes.general.submit}${Routes.general.cancellation}`,
      params
    );
}
export default new PharmacistController();
